import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import shell from '@/styles/authShell.module.css';
import AuthSplitLayout from '@/components/auth/AuthSplitLayout';
import { FORGOT_PANEL } from '@/data/authPanels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  usePageMeta(PAGE_META.forgotPassword);

  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (response.ok) { setSent(true); toast.success('Password reset link sent!'); }
      else toast.error('Failed to send reset link');
    } catch { toast.error('Failed to send reset link'); }
    finally { setLoading(false); }
  };

  return (
    <AuthSplitLayout
      testId="forgot-password-page"
      {...FORGOT_PANEL}
      topRight={
        <button
          type="button"
          className={`${shared.btn} ${shared.btnQuiet}`}
          onClick={() => navigate('/login')}
          data-testid="back-to-login"
        >
          <ArrowLeft size={15} aria-hidden="true" /> Back to sign in
        </button>
      }
    >
      <div data-testid="forgot-password-card">
        <div className={sent ? shell.head : shell.headStart}>
            {!sent ? (
              <>
                <h1 className={shell.title}>Forgot Password?</h1>
                <p className={shell.subtitle}>
                  Enter your email and we&apos;ll send you a reset link
                </p>
              </>
            ) : (
              <>
                <div className={shell.statusIcon}>
                  <CheckCircle size={36} aria-hidden="true" />
                </div>
                <h1 className={shell.title}>Check Your Email</h1>
                <p className={shell.subtitle}>
                  We&apos;ve sent a password reset link to <strong>{email}</strong>
                </p>
              </>
            )}
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} data-testid="forgot-password-form">
              <div className={formStyles.field}>
                <label htmlFor="email" className={formStyles.label}>Email Address</label>
                <div className={formStyles.adorned}>
                  <Mail size={17} className={formStyles.adornedIcon} aria-hidden="true" />
                  <input
                    id="email"
                    type="email"
                    className={`${formStyles.control} ${formStyles.withIcon}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
              <button
                type="submit"
                className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                disabled={loading}
                data-testid="submit-reset-button"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          ) : (
            <>
              <div className={`${shell.banner} ${shell.bannerCentre}`}>
                Didn&apos;t receive the email? Check your spam folder or{' '}
                <button type="button" className={shell.link} onClick={() => setSent(false)}>try again</button>
              </div>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                onClick={() => navigate('/login')}
                data-testid="back-to-login-button"
              >
                Back to Login
              </button>
            </>
          )}

          {!sent && (
            <p className={shell.altText}>
              Remember your password?{' '}
              <button type="button" className={shell.link} onClick={() => navigate('/login')}>Sign in</button>
            </p>
          )}
      </div>
    </AuthSplitLayout>
  );
};

export default ForgotPassword;
